import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GenieChat, deleteGenieChat } from '../api/client'
import ConfirmationModal from './ConfirmationModal'
import ChatListRow from './ChatListRow'
import { useNotify } from './notice/useNotify'

interface GenieChatListProps {
  chats: GenieChat[]
  emptyMessage?: string
  emptySubMessage?: string
  onChatDeleted?: () => void
}

const GenieChatList: React.FC<GenieChatListProps> = ({
  chats,
  emptyMessage = "No genie conversations yet.",
  emptySubMessage = "Start a chat to create projects and features with AI assistance.",
  onChatDeleted
}) => {
  const navigate = useNavigate()
  const notify = useNotify()
  const [deletingChatId, setDeletingChatId] = useState<number | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const handleDeleteClick = (chatId: number, event: React.MouseEvent) => {
    event.stopPropagation()
    setDeletingChatId(chatId)
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (deletingChatId) {
      try {
        await deleteGenieChat(deletingChatId)
        setShowDeleteConfirm(false)
        setDeletingChatId(null)
        if (onChatDeleted) {
          onChatDeleted()
        }
      } catch (error) {
        console.error('Error deleting chat:', error)
        notify.error('Failed to delete chat. Please try again.')
      }
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false)
    setDeletingChatId(null)
  }


  return (
    <div>
      {chats.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>{emptyMessage}</p>
          {emptySubMessage && <p className="text-sm mt-2">{emptySubMessage}</p>}
        </div>
      ) : (
        <div className="space-y-4">
          {chats.map(chat => (
            <ChatListRow
              key={chat.id}
              title={
                <>
                  <span className="mr-2">✨</span>
                  {chat.title || `Genie Chat #${chat.id}`}
                </>
              }
              meta={<>Started {new Date(chat.created_at).toLocaleDateString()}</>}
              onContinue={() => navigate(`/genie/${chat.id}`)}
              onDelete={(e) => handleDeleteClick(chat.id, e)}
            />
          ))}
        </div>
      )}
      
      {showDeleteConfirm && (
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Delete Chat"
          message="Are you sure you want to delete this chat? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
    </div>
  )
}

export default GenieChatList